import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  computed,
} from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import {
  apiToastError,
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import constants, { PRODUCTS_LIMIT, wishlistAction } from '@/constants'
import store from '@/store'
import {
  DELETE_COLLECTION_PRODUCT,
  FETCH_COLLECTION_PRODUCT_BY_ID,
  GET_RELATED_PRODUCTS,
} from '@/store/modules/collection.module'
import {
  SET_SELECTION_NAME,
  FETCH_WISHLIST_PRODUCTS,
} from '@/store/modules/shop.module'
import { useEcommerce, useEcommerceUi } from '@/views/apps/useEcommerce'
import {
  elementInViewport,
  formatObject,
  getWishlistTitle,
} from '@core/utils/utils'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import { INITIALIZE_EDIT_STORE } from '@/store/modules/productEdit.module'
import { handleOptimisticUpdateForWishlist } from '@/@core/comp-functions/wishlistUtils'
import Vue from 'vue'
import { calculateSizeTotalValues } from '@/views/apps/checkout/cart-products-utils'
import { initializeProductEditState } from '@/views/apps/product/detail/initializeProductEditState'

const { formatCurrency, formattedDate } = utils
const { ROLES } = c

export function useProductDetails(route) {
  const {
    toggleProductInWishlist,
    handleCartActionClick,
    parseProductImages,
  } = useEcommerceUi()
  const { addProductInWishlist } = useEcommerce()

  // --- Reactive State ---
  const product = ref(null)
  const suggestedProducts = ref([])
  const productsCount = ref(0)
  const quantity = ref(1)
  const variants = ref([])
  const lineTotalQuantity = ref(1)
  const wishlists = ref([])
  const wishlistId = ref(route.params.wishlistId || '')
  const selectedCurrency = ref('')
  const retailerId = ref(route?.query?.retailerId || '')
  // Start true so the skeleton paints on the very first frame (the popup opens
  // at full size with the shimmer, no small→big flash before the fetch begins).
  const loading = ref(true)
  const continueLoading = ref(false)
  const page = ref(0)
  const isSuggestedProductsLoading = ref(false)
  const loadingIcon = ref(null)
  const isWspHidden = ref(false)
  const isBrandNotAccessible = ref(false)
  const requestStatus = ref(null)
  const inactiveCollection = ref(false)
  const productDetails = ref({})
  const collectionBaseCurrency = ref('')
  const selectedColor = ref(null)

  const colorKey = 'color'

  // --- Computed ---
  const isBrand = computed(
    () => store.getters.entityType === ROLES.BRAND,
  )

  // --- Watch ---
  watch(
    () => product.value,
    newProduct => {
      store.commit(INITIALIZE_EDIT_STORE, initializeProductEditState(newProduct))
    },
  )

  watch(quantity, val => {
    lineTotalQuantity.value = val * calculateSizeTotalValues(product.value.items)
  })

  // --- Data Processing ---
  const setFlags = data => {
    if (data.hasOwnProperty('isCollectionExpired')) {
      inactiveCollection.value = data.isCollectionExpired
    }
    isWspHidden.value = data.hasOwnProperty('isWspHidden') && data.isWspHidden
    requestStatus.value = data.hasOwnProperty('connectionRequestStatus')
      ? data.connectionRequestStatus
      : null
    isBrandNotAccessible.value = data.hasOwnProperty('isBrandNotAccessible')
      && data.isBrandNotAccessible
  }

  const initializeProductFlags = thisProduct => {
    if (!thisProduct.hasOwnProperty('isInWishlist')) {
      thisProduct.isInWishlist = false
    }
    if (!thisProduct.hasOwnProperty('isInCart')) {
      thisProduct.isInCart = false
    }
    if (!thisProduct.hasOwnProperty('wishlists')) {
      thisProduct.wishlists = []
    }
  }

  const setProduct = thisProduct => {
    // Preserve wishlists from current product if API response doesn't include them
    if (!thisProduct.hasOwnProperty('wishlists') && product.value?.wishlists) {
      thisProduct.wishlists = product.value.wishlists
    }
    initializeProductFlags(thisProduct)
    product.value = thisProduct
    if (!selectedCurrency.value) {
      collectionBaseCurrency.value = product.value.currency
    }
  }

  const trackAnalytics = thisProduct => {
    analytics.track(
      constants.TRACKS.PAGES.PRODUCT_DETAILS,
      formatObject(thisProduct),
    )
    const trackAction = isBrand.value
      ? constants.TRACKS.ACTIONS.BRAND_START_VIEWING_PRODUCT
      : constants.TRACKS.ACTIONS.RETAILER_START_VIEWING_PRODUCT
    analytics.track(trackAction, formatObject(productDetails.value))
  }

  const updateProductDetails = response => {
    const data = response.data.data
    productDetails.value = data
    setFlags(data)
    setProduct(data.products[0])
    trackAnalytics(data.products[0])
    if (product.value.isCarton) {
      quantity.value = 1
    }
    lineTotalQuantity.value = quantity.value * calculateSizeTotalValues(product.value.items)
  }

  const updateBreadcrumb = response => {
    if (!Array.isArray(route.meta?.breadcrumb)) return
    route.meta.breadcrumb = route.meta.breadcrumb.map(item => {
      if (item.dynamic === true) {
        if (item.hasOwnProperty('brandName')) {
          item.brandName = response.data.data.brandName
          item.to.params.brandId = response.data.data.brandId
        } else if (item.hasOwnProperty('collectionName')) {
          item.collectionName = response.data.data.collectionName
        } else if (item.hasOwnProperty('productName')) {
          item.productName = product.value.name
        }
      }
      return item
    })
  }

  const handleFetchError = error => {
    loading.value = false
    if (error?.response?.status === 404) {
      product.value = undefined
    }
  }

  // --- Fetch Functions ---
  const processWishlistsResponse = res => {
    if (res?.data?.data?.length) {
      wishlists.value = res.data.data.filter(
        wishlist => !wishlist.isReadOnly,
      )
    }
  }

  const fetchWishlists = async () => {
    const res = await store.dispatch(FETCH_WISHLIST_PRODUCTS, {
      collectionId: route?.params?.collectionId,
    })
    processWishlistsResponse(res)
  }

  // Monotonic load token. Each fresh load() bumps it; in-flight fetches capture
  // the token and discard their result if a newer load has started. Prevents a
  // slow response for an earlier product from overwriting a newer one when the
  // standalone page reuses this composable across rapid recommended-product nav.
  let loadSeq = 0

  const fetchProduct = (isLiveUpdate = false, token = loadSeq) => {
    loading.value = !isLiveUpdate
    const productId = route.params.id
    const params = {}
    if (wishlistId.value) {
      params.wishlistId = wishlistId.value
    }
    if (selectedCurrency.value) {
      params.currency = selectedCurrency.value
    }
    if (retailerId.value) {
      params.retailerId = retailerId.value
    }
    return store
      .dispatch(FETCH_COLLECTION_PRODUCT_BY_ID, { productId, params })
      .then(response => {
        if (token !== loadSeq) return // a newer load started — drop stale result
        updateProductDetails(response)
        updateBreadcrumb(response)
        loading.value = false
      })
      .catch(err => {
        if (token !== loadSeq) return
        handleFetchError(err)
      })
  }

  const buildSuggestedStylesParams = () => {
    const params = {
      page: page.value,
      limit: PRODUCTS_LIMIT,
    }
    if (wishlistId.value) {
      params.wishlistId = wishlistId.value
    }
    return params
  }

  const extractUniqueColors = items => {
    const seen = new Set()
    return items.reduce((uniqueColors, item) => {
      if (!seen.has(item.color)) {
        seen.add(item.color)
        uniqueColors.push({
          color: item.color,
          colorFamily: item.colorFamily,
          hexCode: item.hexCode,
        })
      }
      return uniqueColors
    }, [])
  }

  const enrichProductsWithColors = products => {
    products.forEach(p => {
      p.quantity = p.quantity || 1
      p.colors = extractUniqueColors(p.items)
      if (!p.wishlists) {
        p.wishlists = []
      }
    })
  }

  const updatePaginationState = (products, totalCount, currentPage) => {
    if (currentPage === 0) {
      productsCount.value = 0
    }
    productsCount.value += products.length
    continueLoading.value = totalCount > productsCount.value
  }

  const mergeSuggestedProducts = (products, currentPage) => {
    if (currentPage >= 1) {
      suggestedProducts.value = [...suggestedProducts.value, ...products]
    } else {
      suggestedProducts.value = products
    }
  }

  const extractSuggestedStylesData = res => {
    const responseData = res?.data?.data
    return {
      products: responseData?.products || [],
      totalCount: responseData?.count || 0,
    }
  }

  const parseSuggestedStylesResponse = (res, currentPage) => {
    const { products, totalCount } = extractSuggestedStylesData(res)
    enrichProductsWithColors(products)
    updatePaginationState(products, totalCount, currentPage)
    mergeSuggestedProducts(products, currentPage)
  }

  const fetchSuggestedStyles = async (isLiveUpdate = false, token = loadSeq) => {
    isSuggestedProductsLoading.value = !isLiveUpdate
    const productId = route.params.id
    const params = buildSuggestedStylesParams()
    try {
      const res = await store.dispatch(GET_RELATED_PRODUCTS, { productId, params })
      if (token !== loadSeq) return // a newer load started — drop stale result
      parseSuggestedStylesResponse(res, params.page)
    } catch (_err) {
      // Silently handle error — loading state is reset in finally
    } finally {
      if (token === loadSeq) isSuggestedProductsLoading.value = false
    }
  }

  const loadProductDetails = async (isLiveUpdate = false) => {
    const token = ++loadSeq
    // Fresh load (not a live/socket refresh) → show the skeleton immediately and
    // reset recommendation pagination so it starts from page 0 with no stale
    // appended suggestions. Flipping `loading` up-front matters when the
    // standalone page reuses this composable for a different product: otherwise
    // the previous product stays on screen until fetchProduct resolves. Without
    // the pagination reset, an elevated `page` (after infinite scroll) would make
    // fetchSuggestedStyles fetch the wrong page and APPEND the prior product's
    // related items.
    if (!isLiveUpdate) {
      loading.value = true
      page.value = 0
      productsCount.value = 0
      suggestedProducts.value = []
      continueLoading.value = false
    }
    await fetchWishlists()
    if (token !== loadSeq) return // superseded by a newer load
    await fetchProduct(isLiveUpdate, token)
    if (token !== loadSeq) return
    await fetchSuggestedStyles(isLiveUpdate, token)
  }

  const filterByCurrency = (currency = '') => {
    selectedCurrency.value = currency || ''
    loadProductDetails(true)
  }

  // --- Variant Logic ---
  const getProductItems = items => {
    const productItems = []
    items.forEach(item => {
      for (const k in item) {
        if (item.hasOwnProperty(k) && k !== colorKey) {
          productItems.push({
            sku: item[k].skuId,
            value: item[k].value,
          })
        }
      }
    })
    return productItems
  }

  const updateVariants = vas => {
    variants.value = vas
    const updatedItems = getProductItems(vas)
    product.value.items = updatedItems
    lineTotalQuantity.value = calculateSizeTotalValues(updatedItems)
  }

  // --- Wishlist/Cart Actions ---
  const reactToggleProductInWishlist = async (payload = {}) => {
    const result = await toggleProductInWishlist(payload)
    if (result) {
      product.value = { ...product.value, ...result }
      analytics.track(
        constants.TRACKS.ACTIONS.RETAILER_ADDS_TO_WISHLIST_FROM_PP,
        formatObject({
          collectionName: productDetails.value?.collectionName,
          createdBy: ROLES.RETAILER,
        }),
      )
      loadProductDetails(true)
    }
  }

  const updateProductAfterConflict = updatedProduct => {
    updatedProduct.isInCart = true
    product.value = { ...product.value, ...updatedProduct }
    analytics.track(
      constants.TRACKS.ACTIONS.ADD_TO_CART,
      formatObject(updatedProduct),
    )
    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_ADDS_TO_CART_FROM_PP,
      formatObject({ collectionName: productDetails.value?.collectionName }),
    )
  }

  const reactHandleCartActionClick = async prod => {
    const result = await handleCartActionClick({
      product: prod,
      quantity: quantity.value,
      items: variants.value,
      location: 'productDetails',
      conflictResolveCallback: 'updateProductAfterConflict',
    })
    if (result) {
      product.value = { ...product.value, ...result }
      analytics.track(
        constants.TRACKS.ACTIONS.RETAILER_ADDS_TO_CART_FROM_PP,
        formatObject({ collectionName: productDetails.value?.collectionName }),
      )
    }
  }

  const onSelectWishlistOnProduct = (prod, wishlist) => {
    const payload = {
      product: prod,
      wishlist,
      wishlistId: wishlist._id,
      wishlistName: wishlist.name,
      action: store.getters.isRetailer
        ? wishlistAction.ADD_TO_WISHLIST
        : wishlistAction.ADD_BY_BRAND,
    }
    if (prod.wishlists?.includes(wishlist._id)) {
      payload.action = wishlistAction.REMOVE_FROM_WISHLIST
    }

    const updatedProduct = handleOptimisticUpdateForWishlist({
      product: payload.product,
      wishlistId: payload.wishlistId,
      action: payload.action,
    })

    if (product.value._id === updatedProduct._id) {
      product.value = updatedProduct
    } else {
      Vue.set(suggestedProducts.value, suggestedProducts.value.findIndex(p => p._id === updatedProduct._id), updatedProduct)
    }

    reactToggleProductInWishlist(payload)
  }

  const updateWishlist = () => {
    if (quantity.value === 0) {
      apiToastError('Please enter quantity')
      return
    }
    let valueEntered = false
    const _items = []
    product.value.items.forEach((item, itemKey) => {
      let _values = []
      variants.value.forEach((variant, k) => {
        if (k === itemKey) {
          _values = Object.values(variant).filter(v => !isNaN(parseInt(v)))
          const zeroed = _values.filter(v => v === 0)
          if (zeroed.length !== _values.length) valueEntered = true
        }
      })
      _items.push({
        itemId: item._id,
        values: _values,
      })
    })
    if (valueEntered) {
      const productPayload = {
        productId: product.value._id,
        quantity: quantity.value,
      }
      if (!product.value.isCarton) {
        productPayload.items = _items
      }
      addProductInWishlist({
        collectionId: product.value.collectionId,
        products: [productPayload],
      })
        .then(res => {
          apiToastSuccess(
            res.data.message
            || `Added product to ${getWishlistTitle()}!`,
          )
          store.commit(SET_SELECTION_NAME, '')
        })
        .catch(() => { })
    } else {
      apiToastError('Please select an item')
    }
  }

  // --- Product CRUD ---
  const removeProduct = (shouldProceed = false) => {
    const params = {}
    if (shouldProceed) {
      params.shouldProceed = true
    }
    if (product.value._id) {
      return store
        .dispatch(DELETE_COLLECTION_PRODUCT, {
          productId: product.value._id,
          params,
        })
        .then(res => {
          apiToastSuccess(res.data.message)
          return { success: true }
        })
    } else {
      apiToastWarning('Product Not Valid')
      return Promise.resolve({ success: false })
    }
  }

  const copyProductLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        apiToastSuccess('Product link copied')
      })
      .catch(err => {
        apiToastWarning(err)
      })
  }

  // --- Infinite Scroll ---
  const onScrollLoader = () => {
    if (
      !isSuggestedProductsLoading.value
      && elementInViewport(loadingIcon.value)
    ) {
      page.value++
      fetchSuggestedStyles()
    }
  }

  onMounted(() => {
    window.addEventListener('scroll', onScrollLoader)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', onScrollLoader)
  })

  // --- Init ---
  loadProductDetails()

  return {
    // State
    product,
    suggestedProducts,
    quantity,
    variants,
    lineTotalQuantity,
    wishlists,
    wishlistId,
    selectedCurrency,
    retailerId,
    loading,
    continueLoading,
    page,
    isSuggestedProductsLoading,
    loadingIcon,
    isWspHidden,
    isBrandNotAccessible,
    requestStatus,
    inactiveCollection,
    productDetails,
    collectionBaseCurrency,
    selectedColor,

    // Methods
    fetchProduct,
    fetchSuggestedStyles,
    loadProductDetails,
    filterByCurrency,
    updateVariants,
    reactToggleProductInWishlist,
    reactHandleCartActionClick,
    onSelectWishlistOnProduct,
    updateProductAfterConflict,
    updateWishlist,
    removeProduct,
    copyProductLink,

    // Ecommerce UI passthrough
    toggleProductInWishlist,
    handleCartActionClick,
    parseProductImages,

    // Utilities
    formattedDate,
    formatCurrency,
  }
}
